import styles from '@components/components/CmsComponents/components/CmsProducts/CmsProducts.module.scss';
import ProductCardSlider from '@components/components/Slider/ProductCardSlider/ProductCardSlider';
import { useLocalStorage } from '@components/hooks';
import fetchData from '@components/components/ProductRecommender/hooks/fetchData';
import debounce from 'lodash.debounce';
import ProductDecider from '@components/components/ProductComponents/components/ProductDecider/ProductDecider';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';

const CmsProducts = ({
  products: externProducts,
  default_aktions_code: defaultAktionsCode = '',
}) => {
  const productCardsRef = useRef(null);
  const [storage] = useLocalStorage('address-check');
  const [products, setProducts] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [maxCardHeight, setMaxCardHeight] = useState(0);
  const hasPromo = products.reduce((acc, i) => acc || !!i.eyecatcher, false);
  const [storageTkconnid] = useLocalStorage('tkconnid');

  const getNewUrlOptions = productKey => {
    let olavKeysForURL;
    let olavKeys = {};

    if (storage?.ID) {
      olavKeys = storage?.ID;
      olavKeysForURL = `${olavKeys?.gebaeudeKey}/${olavKeys?.gebaeudeKeyIdent}/${olavKeys?.anlageAdressKey}/${olavKeys?.anlageKey}`;
    } else {
      olavKeysForURL = storage?.currentAddress;
    }
    const isSuperschnell = products.reduce(
      (acc, item) => acc || item.type === 'superschnell',
      false,
    );

    const gaa = olavKeys?.gebaeudeKey ?? storage?.currentAddress?.split('/')[0];

    //add tkconnid (door identification)
    if (isSuperschnell) {
      return `?tarif=[key:${productKey}]&gaa=${gaa}&tkconnid=${storageTkconnid?.tkconnid}`;
    } else {
      return `?tarif=[key:${productKey}]&newAddress=${olavKeysForURL}`;
    }
  };

  useEffect(() => {
    if (externProducts) {
      setProducts(externProducts);
    }
  }, [externProducts]);

  // calculate max card height
  useEffect(() => {
    if (products) {
      Object.values(products)?.every(prod => {
        if (prod?.eyecatcher?.eyecatcher_valid_from && prod?.recommended) {
          setMaxCardHeight(1);

          return false;
        }

        if (
          maxCardHeight < 1 &&
          (prod?.eyecatcher?.eyecatcher_valid_from || prod?.recommended)
        ) {
          setMaxCardHeight(1);
        }

        return true;
      });
    }
  }, [products]);

  // strom and gas products allow users to customize options
  // here we determine the current options for a product
  const getUserOptionForProduct = product => {
    const exists = userOptions.findIndex(e => e.key === product.key);

    if (exists !== -1) {
      return userOptions[exists];
    }

    return {
      key: product.key,
      bindung: product.content.bindung,
      mixtype: product.content.mixtype,
      capprice: product.content.capprice,
    };
  };

  const debouncedFetchData = useCallback(
    debounce(params => {
      fetchData(params.type, params).then(({ data }) => {
        setProducts(data || []);
      });
    }, 500),
    [userOptions],
  );

  // strom and gas products allow users to customize options
  // here we handle the change of an option
  const handleDropdownChange = ({ product, option, value }) => {
    const copyOptions = [...userOptions];

    setUserOptions(() => {
      const exists = copyOptions.findIndex(e => e.key === product.key);

      if (exists !== -1) {
        copyOptions[exists][option] = value;
      } else {
        copyOptions.push({
          key: product.key,
          bindung: option === 'bindung' ? value : product.content.bindung,
          mixtype: option === 'mixtype' ? value : product.content.mixtype,
          capprice: option === 'capprice' ? value : product.content.capprice,
          aktionsCode: option === 'aktionsCode' ? value : defaultAktionsCode,
        });
      }

      return copyOptions;
    });

    // fetch new products to update prices after dropdown change
    const params = { options: copyOptions, type: product.type };
    debouncedFetchData(params);
  };

  // set breakpoints for old product cards
  // new product have default breakpoints set
  const sliderBreakpoints =
    externProducts[0]?.type === 'tanke' ||
    externProducts[0]?.type === 'business_tanke' ||
    externProducts[0]?.type === 'superschnell'
      ? {
          576: {
            slidesPerView: 1.3,
          },
          768: {
            slidesPerView: 1.4,
          },
          992: {
            slidesPerView: 2,
          },
          1200: {
            slidesPerView: 3,
          },
        }
      : undefined;

  return (
    <div className={styles.slider}>
      <ProductCardSlider
        ref={productCardsRef}
        sliderBreakpoints={sliderBreakpoints}
      >
        {products?.map(product => {
          const newUrlOptions = getNewUrlOptions(product?.product_key);
          return (
            <ProductDecider
              getUserOptionForProduct={getUserOptionForProduct}
              handleDropdownChange={handleDropdownChange}
              hasPromo={hasPromo}
              key={`${product.key}`}
              maxEyecatcherHeight={maxCardHeight}
              newUrlOptions={newUrlOptions}
              product={product}
              productCardsRef={productCardsRef}
              productOption={getUserOptionForProduct(product)}
              type={product.type}
            />
          );
        })}
      </ProductCardSlider>
    </div>
  );
};

CmsProducts.propTypes = {
  default_aktions_code: PropTypes.string,
  // exact shape of products depends on the product type
  // eslint-disable-next-line react/forbid-prop-types
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default CmsProducts;
